import { useQuery } from '@tanstack/react-query';
import { CanvasApi } from './canvasApi';

export interface SlugValidationResult {
  slug: string;
  available: boolean;
}

// Hook to validate canvas slug availability
export const useCanvasSlugValidation = (
  accountId: string,
  slug: string,
  enabled = true,
) => {
  return useQuery<SlugValidationResult>({
    queryKey: ['canvas-slug-validation', accountId, slug],
    queryFn: async () => {
      if (!slug || slug.trim() === '') {
        return { slug: '', available: true };
      }

      const response = await CanvasApi.validateCanvasSlug(accountId, slug);

      // The API returns JSON:API format with data.attributes
      if (response && response.data && response.data.attributes) {
        return {
          slug: response.data.attributes.slug,
          available: response.data.attributes.available,
        };
      }

      // Fallback - assume slug is not available if we can't parse the response
      return { slug: slug, available: false };
    },
    enabled: enabled && !!slug && slug.trim() !== '',
    staleTime: 30000, // Cache for 30 seconds
    retry: false,
  });
};

// Hook to validate page slug availability
export const usePageSlugValidation = (
  accountId: string,
  canvasId: string,
  slug: string,
  enabled = true,
) => {
  return useQuery<SlugValidationResult>({
    queryKey: ['page-slug-validation', accountId, canvasId, slug],
    queryFn: async () => {
      if (!slug || slug.trim() === '') {
        return { slug: '', available: true };
      }

      const response = await CanvasApi.validatePageSlug(
        accountId,
        canvasId,
        slug,
      );

      // The API returns JSON:API format with data.attributes
      if (response && response.data && response.data.attributes) {
        return {
          slug: response.data.attributes.slug,
          available: response.data.attributes.available,
        };
      }

      // Fallback - assume slug is not available if we can't parse the response
      return { slug: slug, available: false };
    },
    enabled: enabled && !!canvasId && !!slug && slug.trim() !== '',
    staleTime: 30000, // Cache for 30 seconds
    retry: false,
  });
};

// Utility function to normalize slug (same as backend)
export const normalizeSlug = (input: string): string => {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .trim();
};

// Utility function to generate slug from name
export const generateSlugFromName = (name: string): string => {
  return normalizeSlug(name);
};
